import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel, InputAdornment,
  Radio, RadioGroup,
  Switch,
  TextField,
} from '@material-ui/core';
import CopyIcon from '@material-ui/icons/FileCopy';
import RefreshIcon from '@material-ui/icons/Refresh';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';

import * as actions from '../../../state';
import { saveBoardSettings } from '../../../events';
import styles from './styles.module.css';
import { generateId, parseUrl } from '../../../utils';
import ShareDialogAccessControl from './ShareDialogAccessControl';

const messages = defineMessages({
  SHARE_BOARD: {
    id: 'SHARE_BOARD',
    defaultMessage: 'Share Board',
  },
  SHARE_WITH_LABEL: {
    id: 'SHARE_WITH_LABEL',
    defaultMessage: 'Share board with...',
  },
  SHARE_WITH_ANYONE: {
    id: 'SHARE_WITH_ANYONE',
    defaultMessage: 'Anyone in your organization',
  },
  SHARE_WITH_SELECT: {
    id: 'SHARE_WITH_SELECT',
    defaultMessage: 'Only specific groups or people',
  },
  ALLOW_ORG_EDIT: {
    id: 'ALLOW_ORG_EDIT',
    defaultMessage: 'Allow anyone in your organization to edit',
  },
  PUBLIC_LINK_LABEL: {
    id: 'PUBLIC_LINK_LABEL',
    defaultMessage: 'Share a view-only public link',
  },
  PUBLIC_LINK_HELPER: {
    id: 'PUBLIC_LINK_HELPER',
    defaultMessage: 'Links can be expired by generating a new link.',
  },
  NO_PUBLIC_LINK: {
    id: 'NO_PUBLIC_LINK',
    defaultMessage: 'no link has been generated',
  },
  CANCEL: {
    id: 'CANCEL',
    defaultMessage: 'Cancel',
  },
  SAVE: {
    id: 'SAVE',
    defaultMessage: 'Save',
  },
  LINK_COPIED: {
    id: 'LINK_COPIED',
    defaultMessage: 'The public link has been copied to your clipboard.',
  },
});

function ShareDialog({ intl }) {
  const dispatch = useDispatch();

  const layout = useSelector(actions.selectLayout);
  const board = useSelector(actions.readStateBoard);
  const publicIdLengthConfig = useSelector((state) => actions.selectConfig(state, 'ANALYTICS_PUBLIC_LINK_ID_LENGTH'));
  const publicLinkEnabledConfig = useSelector((state) => actions.selectConfig(state, 'ANALYTICS_PUBLIC_LINK_ENABLED'));

  const [value, setValue] = useState(0);
  const [origShareSettings, setOrigShareSettings] = useState({
    public_id: null,
    share_type: null,
    share_details: null,
  });

  const generatePublicLink = () => {
    board.public_id = generateId(publicIdLengthConfig || 8);
    setValue(value + 1);
  };

  useEffect(() => {
    generatePublicLink();
  }, []);

  useEffect(() => {
    if (layout.showShareDialog) {
      setOrigShareSettings({
        public_id: board.public_id,
        share_type: board.share_type,
        share_details: board.share_details,
      });
    }
  }, [layout.showShareDialog]);

  const selectLink = () => {
    window.getSelection().selectAllChildren(document.getElementById('shareLinkGenerate'));
  };

  const copyPublicLink = () => {
    selectLink();

    document.execCommand('copy');

    dispatch(actions.showToaster({
      show: true,
      message: intl.formatMessage({ ...messages.LINK_COPIED }),
      type: 'success',
    }));
  };

  const toggleEdit = () => {
    board.share_details.edit_enabled = !board.share_details.edit_enabled;
    setValue(value + 1);
  };

  const handleTypeChange = (event) => {
    const shareType = event.target.value;
    board.share_type = shareType;
    board.share_details = shareType === 'select' ? [] : {};
    setValue(value + 1);
  };

  const handleClose = (buttonClicked) => {
    if (buttonClicked) {
      board.public_id = origShareSettings.public_id;
      board.share_type = origShareSettings.share_type;
      board.share_details = origShareSettings.share_details;
      dispatch(actions.showShareDialog(false));
    }
  };

  const handleSave = () => {
    dispatch(saveBoardSettings());
    dispatch(actions.showShareDialog(false));
  };

  const { baseUrl } = parseUrl(window.location);

  if (!board.share_type) { // default
    board.share_type = 'select';
    board.share_details = [];
  }

  return (
    <Dialog
      onClose={handleClose}
      open={layout.showShareDialog || false}
      classes={{
        root: styles.shareDialog,
        paperScrollPaper: styles.shareDialogPaper,
      }}
    >
      <DialogTitle>
        <FormattedMessage {...messages.SHARE_BOARD} />
      </DialogTitle>

      <DialogContent className={styles.shareDialogContent}>
        <DialogContentText>
          <FormattedMessage {...messages.SHARE_WITH_LABEL} />
        </DialogContentText>

        <FormControl component="fieldset">
          <RadioGroup
            id="shareWithRadio"
            name="shareWith"
            value={board.share_type}
            onChange={handleTypeChange}
          >
            <FormControlLabel
              value="select"
              control={<Radio />}
              label={<FormattedMessage {...messages.SHARE_WITH_SELECT} />}
            />
            <FormControlLabel
              value="domain"
              control={<Radio />}
              label={<FormattedMessage {...messages.SHARE_WITH_ANYONE} />}
            />
          </RadioGroup>
        </FormControl>

        {board.share_type === 'select'
          && <ShareDialogAccessControl />}

        {board.share_type === 'domain'
          && (
            <FormControl className={styles.allowOrgEdit}>
              <InputLabel shrink htmlFor="editEnabled">
                <FormattedMessage {...messages.ALLOW_ORG_EDIT} />
              </InputLabel>

              <Switch
                value="editEnabled"
                checked={board.share_details.edit_enabled}
                onChange={toggleEdit}
                inputProps={{
                  name: 'editEnabled',
                  id: 'public-link-enabled-switch',
                }}
              />
            </FormControl>
          )}

        {publicLinkEnabledConfig && publicLinkEnabledConfig === 'yes' && (
          <div>
            <hr className={styles.shareLinkDivider} />

            <DialogContentText>
              <FormattedMessage {...messages.PUBLIC_LINK_LABEL} />
            </DialogContentText>

            <TextField
              id="shareLinkInput"
              helperText={<FormattedMessage {...messages.PUBLIC_LINK_HELPER} />}
              disabled
              onClick={selectLink}
              className={styles.shareLinkInput}
              InputProps={{
                startAdornment: (
                  <InputAdornment
                    id="shareLinkGenerate"
                    className={styles.shareLinkGenerate}
                    position="start"
                  >
                    <RefreshIcon onClick={generatePublicLink} className={styles.generatePublicLink} />
                    {board.public_id
                      ? `${baseUrl}/p/${board.public_id}`
                      : <FormattedMessage {...messages.NO_PUBLIC_LINK} />}
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment id="shareLinkCopy" position="end" onClick={copyPublicLink}>
                    <CopyIcon className={styles.copyPublicLink} />
                  </InputAdornment>
                ),
              }}
            />
          </div>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          <FormattedMessage {...messages.CANCEL} />
        </Button>

        <Button onClick={handleSave} color="primary">
          <FormattedMessage {...messages.SAVE} />
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default injectIntl(ShareDialog);
